// src/modules/Admin/pages/Jobs/JobEditor.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  FaArrowLeft,
  FaSave,
  FaEye,
  FaTrash,
  FaBriefcase,
  FaMapMarkerAlt,
  FaClock,
  FaLaptopHouse,
  FaGraduationCap,
  FaMoneyBillWave,
  FaFileAlt,
  FaGift,
  FaLanguage
} from 'react-icons/fa';

// Styled Components
const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #666;
  text-decoration: none;
  font-size: 14px;
  transition: color 200ms;

  &:hover {
    color: #111;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #111;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: ${props => props.$variant === 'secondary' ? 'white' : props.$variant === 'danger' ? '#dc2626' : '#111'};
  color: ${props => props.$variant === 'secondary' ? '#333' : 'white'};
  border: ${props => props.$variant === 'secondary' ? '1px solid #e5e5e5' : 'none'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  &:hover:not(:disabled) {
    background: ${props => props.$variant === 'secondary' ? '#f5f5f5' : props.$variant === 'danger' ? '#b91c1c' : '#333'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 24px;
  margin-bottom: 24px;
`;

const CardTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #111;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;

  svg {
    color: #666;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$cols || '1fr'};
  gap: 20px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #555;

  span {
    color: #dc2626;
  }
`;

const Input = styled.input`
  padding: 12px 14px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  transition: all 200ms;

  &:focus {
    outline: none;
    border-color: #111;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }

  &::placeholder {
    color: #999;
  }
`;

const Select = styled.select`
  padding: 12px 14px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #111;
  }
`;

const Textarea = styled.textarea`
  padding: 12px 14px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  min-height: ${props => props.$height || '150px'};
  resize: vertical;
  font-family: inherit;
  line-height: 1.6;

  &:focus {
    outline: none;
    border-color: #111;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }

  &::placeholder {
    color: #999;
  }
`;

const HelperText = styled.span`
  font-size: 12px;
  color: #999;
`;

const SlugPreview = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;

  code {
    background: #f5f5f5;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: monospace;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #e5e5e5;
  margin-bottom: 20px;
`;

const Tab = styled.button`
  padding: 10px 16px;
  border: none;
  background: ${props => props.$active ? '#f5f5f5' : 'transparent'};
  color: ${props => props.$active ? '#111' : '#666'};
  font-size: 13px;
  font-weight: ${props => props.$active ? '600' : '400'};
  cursor: pointer;
  border-radius: 8px 8px 0 0;
  transition: all 200ms;

  &:hover {
    background: #f5f5f5;
  }
`;

const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 16px 24px;
  background: ${props => props.$type === 'error' ? '#dc2626' : '#10b981'};
  color: white;
  border-radius: 8px;
  font-size: 14px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  z-index: 100;
`;

// Departamentos sugeridos
const DEPARTMENTS = [
  'Tecnologia',
  'Engenharia',
  'Comercial',
  'Marketing',
  'Administrativo',
  'Operações',
  'RH',
  'Financeiro'
];

const JobEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('pt');

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    department: '',
    location: 'São Paulo, SP',
    type: 'full-time',
    modality: 'hybrid',
    experience_level: 'mid',
    description: '',
    requirements: '',
    benefits: '',
    salary_range: '',
    status: 'active',
    // Traduções
    title_en: '',
    title_es: '',
    description_en: '',
    description_es: '',
    requirements_en: '',
    requirements_es: '',
    benefits_en: '',
    benefits_es: ''
  });

  useEffect(() => {
    if (isEditing) {
      fetchJob();
    }
  }, [id]);

  const fetchJob = async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      setFormData({
        title: data.title || '',
        slug: data.slug || '',
        department: data.department || '',
        location: data.location || 'São Paulo, SP',
        type: data.type || 'full-time',
        modality: data.modality || 'hybrid',
        experience_level: data.experience_level || 'mid',
        description: data.description || '',
        requirements: data.requirements || '',
        benefits: data.benefits || '',
        salary_range: data.salary_range || '',
        status: data.status || 'active',
        title_en: data.title_en || '',
        title_es: data.title_es || '',
        description_en: data.description_en || '',
        description_es: data.description_es || '',
        requirements_en: data.requirements_en || '',
        requirements_es: data.requirements_es || '',
        benefits_en: data.benefits_en || '',
        benefits_es: data.benefits_es || ''
      });
    } catch (error) {
      console.error('Erro ao buscar vaga:', error);
      showToast('Erro ao carregar vaga', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };

      // Auto-generate slug from title
      if (name === 'title' && !isEditing) {
        newData.slug = generateSlug(value);
      }

      return newData;
    });
  };

  const handleSubmit = async (status = formData.status) => {
    // Validação básica
    if (!formData.title || !formData.department || !formData.description || !formData.requirements) {
      showToast('Preencha todos os campos obrigatórios', 'error');
      return;
    }

    setSaving(true);
    try {
      const jobData = {
        title: formData.title,
        slug: formData.slug || generateSlug(formData.title),
        department: formData.department,
        location: formData.location,
        type: formData.type,
        modality: formData.modality,
        experience_level: formData.experience_level,
        description: formData.description,
        requirements: formData.requirements,
        benefits: formData.benefits || null,
        salary_range: formData.salary_range || null,
        status: status,
        title_en: formData.title_en || null,
        title_es: formData.title_es || null,
        description_en: formData.description_en || null,
        description_es: formData.description_es || null,
        requirements_en: formData.requirements_en || null,
        requirements_es: formData.requirements_es || null,
        benefits_en: formData.benefits_en || null,
        benefits_es: formData.benefits_es || null,
        ...(status === 'active' && !isEditing ? { published_at: new Date().toISOString() } : {}),
        ...(!isEditing ? { created_by: user?.email } : {})
      };

      if (isEditing) {
        const { error } = await supabase
          .from('jobs')
          .update(jobData)
          .eq('id', id);

        if (error) throw error;
        showToast('Vaga atualizada com sucesso');
      } else {
        const { error } = await supabase
          .from('jobs')
          .insert([jobData]);

        if (error) throw error;
        showToast('Vaga criada com sucesso');
        navigate('/admin/vagas');
      }
    } catch (error) {
      console.error('Erro ao salvar vaga:', error);
      showToast(error.message || 'Erro ao salvar vaga', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja excluir esta vaga?')) return;

    try {
      const { error } = await supabase
        .from('jobs')
        .delete()
        .eq('id', id);

      if (error) throw error;
      showToast('Vaga excluída com sucesso');
      navigate('/admin/vagas');
    } catch (error) {
      console.error('Erro ao excluir:', error);
      showToast('Erro ao excluir vaga', 'error');
    }
  };

  if (loading) {
    return (
      <Container>
        <p>Carregando...</p>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div>
          <BackLink to="/admin/vagas">
            <FaArrowLeft />
            Voltar para vagas
          </BackLink>
          <Title>{isEditing ? 'Editar Vaga' : 'Nova Vaga'}</Title>
        </div>

        <Actions>
          {isEditing && (
            <>
              <Button
                $variant="secondary"
                as="a"
                href={`/carreiras/${formData.slug}`}
                target="_blank"
              >
                <FaEye />
                Visualizar
              </Button>
              <Button $variant="danger" onClick={handleDelete}>
                <FaTrash />
                Excluir
              </Button>
            </>
          )}
          <Button onClick={() => handleSubmit('active')} disabled={saving}>
            <FaSave />
            {saving ? 'Salvando...' : (isEditing ? 'Salvar' : 'Publicar')}
          </Button>
        </Actions>
      </Header>

      {/* Informações Básicas */}
      <Card>
        <CardTitle>
          <FaBriefcase />
          Informações da Vaga
        </CardTitle>

        <FormGrid $cols="1fr">
          <FormGroup>
            <Label>Título da Vaga <span>*</span></Label>
            <Input
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Ex: Desenvolvedor Full Stack"
            />
            {formData.slug && (
              <SlugPreview>
                URL: <code>/carreiras/{formData.slug}</code>
              </SlugPreview>
            )}
          </FormGroup>
        </FormGrid>

        <FormGrid $cols="1fr 1fr" style={{ marginTop: 20 }}>
          <FormGroup>
            <Label>Departamento <span>*</span></Label>
            <Select
              name="department"
              value={formData.department}
              onChange={handleChange}
            >
              <option value="">Selecione...</option>
              {DEPARTMENTS.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Localização <span>*</span></Label>
            <Input
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Ex: São Paulo, SP"
            />
          </FormGroup>
        </FormGrid>

        <FormGrid $cols="1fr 1fr 1fr" style={{ marginTop: 20 }}>
          <FormGroup>
            <Label>Tipo de Contrato <span>*</span></Label>
            <Select
              name="type"
              value={formData.type}
              onChange={handleChange}
            >
              <option value="full-time">Tempo Integral</option>
              <option value="part-time">Meio Período</option>
              <option value="contract">Contrato</option>
              <option value="internship">Estágio</option>
              <option value="freelance">Freelance</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Modalidade <span>*</span></Label>
            <Select
              name="modality"
              value={formData.modality}
              onChange={handleChange}
            >
              <option value="on-site">Presencial</option>
              <option value="remote">Remoto</option>
              <option value="hybrid">Híbrido</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Nível de Experiência <span>*</span></Label>
            <Select
              name="experience_level"
              value={formData.experience_level}
              onChange={handleChange}
            >
              <option value="intern">Estágio</option>
              <option value="junior">Júnior</option>
              <option value="mid">Pleno</option>
              <option value="senior">Sênior</option>
              <option value="lead">Líder/Gerente</option>
            </Select>
          </FormGroup>
        </FormGrid>

        <FormGrid $cols="1fr 1fr" style={{ marginTop: 20 }}>
          <FormGroup>
            <Label>Faixa Salarial (opcional)</Label>
            <Input
              name="salary_range"
              value={formData.salary_range}
              onChange={handleChange}
              placeholder="Ex: R$ 8.000 - R$ 12.000"
            />
            <HelperText>Deixe em branco para "A combinar"</HelperText>
          </FormGroup>

          <FormGroup>
            <Label>Status</Label>
            <Select
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="active">Ativa</option>
              <option value="paused">Pausada</option>
              <option value="closed">Encerrada</option>
            </Select>
          </FormGroup>
        </FormGrid>
      </Card>

      {/* Descrição */}
      <Card>
        <CardTitle>
          <FaFileAlt />
          Descrição e Requisitos
        </CardTitle>

        <TabContainer>
          <Tab $active={activeTab === 'pt'} onClick={() => setActiveTab('pt')}>
            Português
          </Tab>
          <Tab $active={activeTab === 'en'} onClick={() => setActiveTab('en')}>
            English
          </Tab>
          <Tab $active={activeTab === 'es'} onClick={() => setActiveTab('es')}>
            Español
          </Tab>
        </TabContainer>

        {activeTab === 'pt' && (
          <>
            <FormGroup>
              <Label>Descrição da Vaga <span>*</span></Label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Descreva as responsabilidades e atividades da vaga... (Suporta Markdown)"
                $height="200px"
              />
              <HelperText>Suporta formatação Markdown</HelperText>
            </FormGroup>

            <FormGroup style={{ marginTop: 20 }}>
              <Label>Requisitos <span>*</span></Label>
              <Textarea
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                placeholder="Liste os requisitos técnicos e comportamentais... (Suporta Markdown)"
                $height="200px"
              />
            </FormGroup>

            <FormGroup style={{ marginTop: 20 }}>
              <Label>Benefícios (opcional)</Label>
              <Textarea
                name="benefits"
                value={formData.benefits}
                onChange={handleChange}
                placeholder="Liste os benefícios oferecidos... (Suporta Markdown)"
                $height="150px"
              />
            </FormGroup>
          </>
        )}

        {activeTab === 'en' && (
          <>
            <FormGroup>
              <Label>Job Title (English)</Label>
              <Input
                name="title_en"
                value={formData.title_en}
                onChange={handleChange}
                placeholder="Ex: Full Stack Developer"
              />
            </FormGroup>

            <FormGroup style={{ marginTop: 20 }}>
              <Label>Job Description (English)</Label>
              <Textarea
                name="description_en"
                value={formData.description_en}
                onChange={handleChange}
                placeholder="Describe the job responsibilities..."
                $height="200px"
              />
            </FormGroup>

            <FormGroup style={{ marginTop: 20 }}>
              <Label>Requirements (English)</Label>
              <Textarea
                name="requirements_en"
                value={formData.requirements_en}
                onChange={handleChange}
                placeholder="List the requirements..."
                $height="200px"
              />
            </FormGroup>

            <FormGroup style={{ marginTop: 20 }}>
              <Label>Benefits (English)</Label>
              <Textarea
                name="benefits_en"
                value={formData.benefits_en}
                onChange={handleChange}
                placeholder="List the benefits..."
                $height="150px"
              />
            </FormGroup>
          </>
        )}

        {activeTab === 'es' && (
          <>
            <FormGroup>
              <Label>Título del Puesto (Español)</Label>
              <Input
                name="title_es"
                value={formData.title_es}
                onChange={handleChange}
                placeholder="Ej: Desarrollador Full Stack"
              />
            </FormGroup>

            <FormGroup style={{ marginTop: 20 }}>
              <Label>Descripción del Puesto (Español)</Label>
              <Textarea
                name="description_es"
                value={formData.description_es}
                onChange={handleChange}
                placeholder="Describe las responsabilidades..."
                $height="200px"
              />
            </FormGroup>

            <FormGroup style={{ marginTop: 20 }}>
              <Label>Requisitos (Español)</Label>
              <Textarea
                name="requirements_es"
                value={formData.requirements_es}
                onChange={handleChange}
                placeholder="Lista los requisitos..."
                $height="200px"
              />
            </FormGroup>

            <FormGroup style={{ marginTop: 20 }}>
              <Label>Beneficios (Español)</Label>
              <Textarea
                name="benefits_es"
                value={formData.benefits_es}
                onChange={handleChange}
                placeholder="Lista los beneficios..."
                $height="150px"
              />
            </FormGroup>
          </>
        )}
      </Card>

      {toast && (
        <Toast $type={toast.type}>
          {toast.message}
        </Toast>
      )}
    </Container>
  );
};

export default JobEditor;
